import { useEffect, useState } from "react";
import { fetchUploadConfig } from "../api/configApi";
import {
  CLIENTS,
  RETAILERS,
  type ClientOption,
  type RetailerOption,
} from "../config/retailerConfig";

export type UploadConfigSource = "api" | "fallback";

export type UploadConfigState = {
  clients: ClientOption[];
  error: string | null;
  isLoading: boolean;
  retailers: RetailerOption[];
  source: UploadConfigSource;
};

type UploadConfigData = {
  clients: ClientOption[];
  retailers: RetailerOption[];
};

const fallbackUploadConfig: UploadConfigData = {
  retailers: RETAILERS,
  clients: CLIENTS,
};

let cachedUploadConfig: UploadConfigData | null = null;
let pendingUploadConfig: Promise<UploadConfigData> | null = null;

function getUploadConfig(): Promise<UploadConfigData> {
  if (cachedUploadConfig) {
    return Promise.resolve(cachedUploadConfig);
  }

  if (!pendingUploadConfig) {
    pendingUploadConfig = fetchUploadConfig()
      .then((config) => {
        cachedUploadConfig = config;
        return config;
      })
      .finally(() => {
        pendingUploadConfig = null;
      });
  }

  return pendingUploadConfig;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unable to load upload config";
}

export function useUploadConfig(): UploadConfigState {
  const [state, setState] = useState<UploadConfigState>(() => {
    const initialConfig = cachedUploadConfig ?? fallbackUploadConfig;
    return {
      ...initialConfig,
      error: null,
      isLoading: cachedUploadConfig === null,
      source: cachedUploadConfig ? "api" : "fallback",
    };
  });

  useEffect(() => {
    let cancelled = false;

    if (cachedUploadConfig) {
      setState({
        ...cachedUploadConfig,
        error: null,
        isLoading: false,
        source: "api",
      });
      return () => {
        cancelled = true;
      };
    }

    setState((current) => ({ ...current, isLoading: true }));

    getUploadConfig()
      .then((config) => {
        if (cancelled) {
          return;
        }

        setState({
          ...config,
          error: null,
          isLoading: false,
          source: "api",
        });
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        setState({
          ...fallbackUploadConfig,
          error: getErrorMessage(error),
          isLoading: false,
          source: "fallback",
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function resetUploadConfigCacheForTests(): void {
  cachedUploadConfig = null;
  pendingUploadConfig = null;
}
